import { Label } from "@/components/ui/label";
import BackNavigator from "@/pages/components/back-navigation";
import { resetPasswordSchema } from "@/lib/form-schema";
import { cn, describeMissingFields } from "@/lib/utils";
import { ChevronLeft } from "lucide-react";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { useForm } from "@tanstack/react-form";
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import type {
  IRegisterResponse,
  IVerifyOTPRegistration,
} from "@/hooks/use-register";
import { useMRegister, useMVerifyOTP } from "@/hooks/use-register";
import { Button } from "@/components/ui/button";
import { emitEvent, EventConstants } from "@/lib/event-emitter";
import { ErrorNotificationType } from "./components/error-notification";
import ScreenRouteNames from "@/constants/screen-route-names";

export default function VerifyEmail() {
  const verifyOTPQuery = useMVerifyOTP();
  const resendOtpQuery = useMRegister();
  const location = useLocation();
  const navigate = useNavigate();

  const getRegisterInfo = () => ({
    ...location.state?.registerInfo,
  });

  const handleResendOtp = () => {
    const resendInfo: IRegisterResponse = getRegisterInfo();
    const resendDetails = Object.fromEntries(
      Object.entries(resendInfo).filter(
        ([k, v]) => v !== "" && k !== "signature",
      ),
    );
    resendOtpQuery.mutate(resendDetails as IRegisterResponse, {
      onSuccess: (data) => {
        if (data.success) {
          navigate(".", {
            replace: true, // update state without new history entry
            state: {
              registerInfo: {
                ...location.state?.registerInfo,
                signature: data?.signature,
              },
            },
          });
          emitEvent(EventConstants.ADD_ERROR, {
            message: data.message,
            type: ErrorNotificationType.INFO,
          });
        }
      },
    });
  };

  const form = useForm({
    defaultValues: {
      otp: "",
      password: "",
      confirmPassword: "",
    },
    onSubmit: ({ value }) => {
      const registerInfo: IVerifyOTPRegistration = {
        ...location.state?.registerInfo,
        enteredOTP: value.otp,
      };
      const registrationDetails = Object.fromEntries(
        Object.entries(registerInfo).filter(([_, v]) => v !== ""),
      );
      verifyOTPQuery.mutate(registrationDetails as IVerifyOTPRegistration, {
        onSuccess: (data) => {
          if (data.success) {
            navigate(`/${ScreenRouteNames.LOGIN.path}`);
          }
        },
      });
    },
  });

  return (
    <main className="flex h-full flex-col items-center justify-center space-y-10 px-8">
      <BackNavigator
        title="Email Verification Code"
        left={
          <Button
            variant="ghost"
            aria-label="Go back"
            className="left-0 h-auto w-fit bg-transparent p-0 hover:bg-transparent hover:shadow-none focus:bg-transparent focus:shadow-none active:bg-transparent"
            onClick={() => {
              if (window.history.length > 1) {
                navigate(-1);
              } else {
                navigate("/nav-error");
              }
            }}
          >
            <ChevronLeft size={20} />
          </Button>
        }
      />
      <div className="flex h-full items-center justify-center bg-white">
        <div className="w-full max-w-sm p-4">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              form.handleSubmit();
            }}
          >
            <div className="flex flex-col">
              <Label className="py-4 text-center text-lg font-bold">
                Check your email
              </Label>
              <Label className="py-4 text-center text-xs text-gray-500">
                Please enter the six digit verification code we sent to
                <span className="mt-2 block text-black">
                  {location.state?.registerInfo.email}
                </span>
              </Label>
            </div>

            <div className="space-y-2">
              <form.Field
                name="otp"
                validators={{
                  onBlur: ({ value }) => {
                    const result =
                      resetPasswordSchema.shape.otp.safeParse(value);
                    return result.success
                      ? undefined
                      : result.error.errors[0].message;
                  },
                }}
              >
                {(field) => (
                  <div className="flex w-full flex-col items-center justify-center space-y-2">
                    <InputOTP
                      maxLength={6}
                      value={field.state.value}
                      onChange={(val) => field.handleChange(val)}
                      onBlur={() => field.handleBlur()}
                    >
                      <InputOTPGroup>
                        <InputOTPSlot index={0} />
                        <InputOTPSlot index={1} />
                        <InputOTPSlot index={2} />
                        <InputOTPSlot index={3} />
                        <InputOTPSlot index={4} />
                        <InputOTPSlot index={5} />
                      </InputOTPGroup>
                    </InputOTP>
                    {field.state.meta.errors[0] && (
                      <em className="w-full text-center text-xs text-red-500">
                        {field.state.meta.errors[0]}
                      </em>
                    )}
                  </div>
                )}
              </form.Field>
            </div>

            {/* Resend OTP */}
            <div className="flex flex-col">
              <div className="flex flex-row items-center justify-center gap-3">
                <span className="py-4 text-center text-[10px] text-gray-500">
                  Didn&apos;t get the email?
                </span>
                <Button
                  type="button"
                  className="w-fit bg-transparent p-0 text-[10px] text-black shadow-none hover:underline"
                  onClick={handleResendOtp}
                >
                  Resend OTP
                </Button>
              </div>
              <form.Subscribe
                selector={(state) => [state.canSubmit, state.values.otp]}
              >
                {([canSubmit, otp]) => {
                  // 3.3.1 Error Identification — A: names the specific empty
                  // field(s) rather than a static list.
                  const requirement =
                    describeMissingFields([
                      { label: "verification code", value: otp },
                    ]) || "Disabled. Fix the errors above to continue.";
                  return (
                    <>
                      <Button
                        type="submit"
                        size="lg"
                        variant="default"
                        className={cn(
                          "my-2 w-full text-sm sm:px-2",
                          !canSubmit && "opacity-50",
                        )}
                        disabled={verifyOTPQuery.isPending}
                        aria-disabled={!canSubmit}
                        aria-describedby={
                          !canSubmit ? "verify-otp-requirement" : undefined
                        }
                      >
                        {verifyOTPQuery.isPending
                          ? "Verifying..."
                          : "Verify OTP"}
                      </Button>
                      {!canSubmit && (
                        <p id="verify-otp-requirement" className="sr-only">
                          {requirement}
                        </p>
                      )}
                    </>
                  );
                }}
              </form.Subscribe>
            </div>
          </form>
        </div>
      </div>
    </main>
  );
}
